import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCircleInfo,
    faUser,
    faEnvelope,
    faPhone,
    faPlus,
    faDownload,
    faEuroSign,
} from "@fortawesome/free-solid-svg-icons";

import PaginatorComponent from "../../../components/general/auxiliar/ui/PaginatorComponent";
import WhatsAppReminder from "../../../components/general/auxiliar/ui/WhatsAppReminder";
import CustomForm from "../../../components/general/form/CustomForm";
import Collapse from "../../../components/general/auxiliar/collapse/Collapse";
import EmptyTable from "../../../components/general/auxiliar/table/EmptyTable";
import LoadingTable from "../../../components/general/auxiliar/table/LoadingTable";
import OverviewTable from "../../../components/general/auxiliar/table/OverviewTable";
import OverviewCardList from "../../../components/general/auxiliar/table/OverviewCard";
import TableBadge from "../../../components/general/auxiliar/table/TableBadge";
import useFormWithDisable from "../../../auxiliar/customHooks/useFormWithDisable";
import usePaginationFilterEffects from "../../../auxiliar/customHooks/usePaginationFilterEffects";
import {
    handleSubmit,
    handleSubmitWithDispatch,
    getTableState,
} from "../../../auxiliar/auxFunctions";
import { downloadPatients, getAllPatients } from "../../../actions/patient";

const OverviewPatients = () => {
    const { register, watch, errors } = useFormWithDisable();
    const writtenFilter = {
        name: watch("name") || "",
        surnames: watch("surnames") || "",
        email: watch("email") || "",
        phone_number: watch("phone_number") || "",
        include_debt: true,
    };

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id: clinicId } = useSelector((state) => state.activeClinic);
    const [patients, setPatients] = useState({ waiting: true });
    const [filter, setFilter] = useState({
        clinic_id: clinicId,
        page: 1,
        per_page: 10,
        order: "name",
    });

    const disabled = usePaginationFilterEffects(
        filter,
        writtenFilter,
        getAllPatients,
        setPatients,
        setFilter,
    );

    // Table columns configuration
    const columns = [
        { label: "Nombre", icon: faUser, width: "w-[20%]" },
        { label: "Apellidos", icon: faUser, cellClass: "hidden xs:table-cell", width: "w-[20%]" },
        { label: "Teléfono", icon: faPhone, cellClass: "hidden sm:table-cell", width: "w-[20%]" },
        { label: "Deuda", icon: faEuroSign, cellClass: "hidden md:table-cell", width: "w-[20%]" },
    ];

    // Function to get row data for table
    const getRowData = (patient, patientId) => {
        // Debt badge logic (similar to OverviewCashRegister)
        const patientDebt = parseFloat(patient.patient_debt || 0);
        const hasDebt = patientDebt > 0;
        const debtVariant = hasDebt ? "warning" : "success";
        const debtText = hasDebt ? `${patientDebt.toFixed(2)}€` : "Pagado";

        return [
            // Nombre (responsive - shows full name on mobile, just name on larger screens)
            <div key={`${patientId}-name`} className="font-semibold text-base-content">
                <span className="truncate" title={patient.name}>
                    {patient.name}
                </span>
            </div>,
            // Apellidos (hidden on mobile)
            <span
                key={`${patientId}-surnames`}
                className="text-base-content/70 truncate"
                title={patient.surnames}
            >
                {patient.surnames}
            </span>,
            // Teléfono (hidden on mobile)
            <div key={`${patientId}-phone`} className="text-base-content/70">
                <div className="flex items-center gap-1">
                    <span>{patient.phone_number || "-"}</span>
                    <WhatsAppReminder link={patient.link} />
                </div>
            </div>,
            // Deuda (hidden on mobile and small screens)
            <TableBadge
                key={`${patientId}-debt`}
                variant={debtVariant}
                size="sm"
                className="text-sm"
            >
                {debtText}
            </TableBadge>,
        ];
    };

    // Function to get detail path
    const getDetailPath = (patientId) => `/patient/${patientId}`;

    // Card render functions
    const renderTitle = (patient) => `${patient.name} ${patient.surnames}`;

    const renderSubtitle = (patient) => (
        <div className="flex items-center gap-2 mt-1">
            <div className="flex items-center gap-1 text-base-content/70">
                <FontAwesomeIcon
                    icon={faEnvelope}
                    className="w-3 h-3 text-base-content/40 flex-shrink-0"
                />
                <span className="text-xs sm:text-sm truncate">{patient.email || "-"}</span>
            </div>
        </div>
    );

    const renderFooter = (patient) => {
        // Debt badge logic for mobile cards
        const patientDebt = parseFloat(patient.patient_debt || 0);
        const hasDebt = patientDebt > 0;
        const debtVariant = hasDebt ? "warning" : "success";
        const debtText = hasDebt ? `${patientDebt.toFixed(2)}€` : "Pagado";

        return (
            <div className="space-y-2 text-xs sm:text-sm">
                <div className="flex items-center gap-2 text-base-content/70"></div>
                {/* Always show debt on mobile cards (consistent with table) */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-base-content/70">
                        <FontAwesomeIcon
                            icon={faPhone}
                            className="w-3 h-3 text-base-content/40 flex-shrink-0"
                        />
                        <div className="flex items-center gap-1">
                            <span>{patient.phone_number || "-"}</span>
                            <WhatsAppReminder link={patient.link} />
                        </div>
                    </div>
                    <TableBadge variant={debtVariant} size="sm">
                        {debtText}
                    </TableBadge>
                </div>
            </div>
        );
    };

    const formConfig = {
        name: {
            condition: true,
            disabled: false,
            type: "text",
            label: "Nombre",
            placeholder: "Nombre",
            name: "name",
        },
        surnames: {
            condition: true,
            disabled: false,
            type: "text",
            label: "Apellidos",
            placeholder: "Apellidos",
            name: "surnames",
        },
        email: {
            condition: true,
            disabled: false,
            type: "text",
            label: "Correo Electrónico",
            placeholder: "paciente@email.com",
            name: "email",
        },
        phone_number: {
            condition: true,
            disabled: false,
            type: "text",
            label: "Número de teléfono",
            placeholder: "+34 XXX XXX XXX",
            name: "phone_number",
        },
    };

    const tableState = getTableState(patients);

    const renderContent = () => {
        if (tableState === "loading") {
            return <LoadingTable message="Cargando pacientes..." />;
        }

        if (tableState === "empty") {
            return (
                <EmptyTable
                    icon={faUser}
                    title="No hay pacientes registrados"
                    description="Añade el primer paciente para comenzar a gestionar tu clínica."
                    buttonText="Nuevo paciente"
                    onButtonClick={handleSubmit(navigate, "/patient/register")}
                />
            );
        }

        return (
            <>
                <OverviewCardList
                    data={patients}
                    navigate={navigate}
                    getDetailPath={getDetailPath}
                    renderTitle={renderTitle}
                    renderSubtitle={renderSubtitle}
                    renderFooter={renderFooter}
                />
                <OverviewTable
                    data={patients}
                    navigate={navigate}
                    columns={columns}
                    getRowData={getRowData}
                    getDetailPath={getDetailPath}
                />
            </>
        );
    };

    return (
        <div className="sm:container p-3 mx-auto mt-5 mb-12">
            <h2 className="sr-only">
                Gestiona los pacientes de tu clínica de fisioterapia en MiFisio Agenda - Pacientes
            </h2>

            <Collapse isMainTitle={true} title="Pacientes" faModality="filter" initialState={true}>
                <form>
                    <CustomForm
                        formConfig={formConfig}
                        errors={errors}
                        register={register}
                        className="grid grid-cols-1 md:grid-cols-2 gap-3"
                    />
                </form>
            </Collapse>

            <div className="mt-6">
                <div className="my-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
                    {/* New patient button */}
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full sm:w-auto">
                        <button
                            className="btn btn-primary btn-sm w-full sm:w-auto"
                            onClick={handleSubmit(navigate, "/patient/register")}
                        >
                            <FontAwesomeIcon icon={faPlus} className="mr-2" />
                            Nuevo paciente
                        </button>

                        {/* Results count and download button */}
                        {tableState === "hasData" && (
                            <button
                                className="btn btn-primary btn-sm w-full sm:w-auto"
                                onClick={handleSubmitWithDispatch(dispatch, downloadPatients, {
                                    ...filter,
                                    ...writtenFilter,
                                })}
                                disabled={patients.pagination?.total_items < 1}
                            >
                                <FontAwesomeIcon icon={faDownload} className="mr-2" />
                                Descargar pacientes
                                <div
                                    className="tooltip tooltip-left ml-1"
                                    data-tip="Se descargarán los pacientes resultantes de los filtros aplicados"
                                >
                                    <FontAwesomeIcon icon={faCircleInfo} />
                                </div>
                            </button>
                        )}
                    </div>

                    {/* Pagination - only render wrapper if there's pagination or total items */}
                    <div className="w-full sm:w-auto">
                        <PaginatorComponent
                            pagination={patients.pagination}
                            setFilter={setFilter}
                            filter={filter}
                            disabled={disabled}
                        />
                    </div>
                </div>

                {renderContent()}

                {/* Pagination for small screens - duplicated to improve UX */}
                <div className="w-full my-5 sm:w-auto lg:hidden">
                    <PaginatorComponent
                        pagination={patients.pagination}
                        setFilter={setFilter}
                        filter={filter}
                        disabled={disabled}
                        showTotalItems={false}
                    />
                </div>
            </div>
        </div>
    );
};

export default OverviewPatients;
